import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { apiSlice } from 'store/api/apiSlice';
import { useGetDataStampQuery } from 'pages/Dashboard/Dashboard.service';

/**
 * How often to re-check the data generation stamp.
 *
 * The dashboard caches aggressively (24h), so a tab left open across the
 * nightly batch would otherwise keep showing yesterday's numbers until a
 * reload. Polling this one tiny endpoint is what detects the rollover. Ten
 * minutes is frequent enough that nobody stares at stale data for long, and the
 * request is trivial (an indexed, partition-pruned, server-cached lookup).
 */
const STAMP_POLL_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Current nightly-batch data generation stamp for the dashboard.
 *
 * Why this exists
 * ---------------
 * All dashboard data is rebuilt by one nightly batch job and is then immutable
 * for the rest of the day. That makes caching for a full day the right call —
 * the only hazard is the nightly rollover, where a long-lived cache would keep
 * serving yesterday's numbers.
 *
 * This hook returns an opaque version string identifying the current
 * generation. Passing it into a query's args makes that query's cache key
 * generation-scoped: during the day the key is stable (so nothing refetches),
 * and when a new generation appears the key changes and the data is fetched
 * exactly once more. Correctness without giving up the cache.
 *
 * Usage
 * -----
 *     const v = useDataVersion();
 *     const { data } = useGetAttendanceNumbersQuery({ ...args, v }, {
 *       skip: !v,          // wait for the stamp so we don't cache under "no version"
 *     });
 *
 * Returns `undefined` until the stamp resolves. Callers should normally `skip`
 * while it is undefined, so a single canonical key is used per generation
 * rather than one request keyed without a version and another with it.
 */
export const useDataVersion = (): string | undefined => {
  // Shared across every caller: RTK Query dedupes this to one request and one
  // poll timer, and the result is reused everywhere.
  const { data } = useGetDataStampQuery(undefined, {
    pollingInterval: STAMP_POLL_MS,
  });
  return data?.version;
};

/**
 * Drop the cached API data whenever the nightly batch publishes a new
 * generation.
 *
 * Threading the version through every query's args would also work, but there
 * are dozens of dashboard queries across four tabs; doing it in one place is
 * both less invasive and impossible to forget. Mount this ONCE near the root of
 * the dashboard.
 *
 * It resets only on an actual change between two known versions — never on
 * first load (there is nothing stale to clear then), so it costs nothing in the
 * common case and does not cause a refetch storm on mount.
 */
export const useDashboardCacheRollover = (): void => {
  const dispatch = useDispatch();
  const version = useDataVersion();
  const seen = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!version) return;
    const previous = seen.current;
    seen.current = version;
    // Only a genuine generation change invalidates; the first observed value
    // is just recorded.
    if (previous && previous !== version) {
      dispatch(apiSlice.util.resetApiState());
    }
  }, [version, dispatch]);
};

export default useDataVersion;
